import { X } from "lucide-react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import type { ShellClient } from "../core/shell";
import type { OutgoingEmailDraft } from "../core/shell-types";
import { parseAddressList } from "./format";

export interface ComposerSeed {
  to?: string;
  subject?: string;
  body?: string;
  inReplyTo?: string[];
  references?: string[];
}

export function Composer({
  shell,
  compact = false,
  seed,
  onClose,
  onSent,
}: {
  shell: ShellClient;
  // Compact: a full-screen sheet (Cancel / Send in a sticky header, fields
  // stacked, the body takes the rest) padded into the safe area; desktop: the
  // bottom-right dialog.
  compact?: boolean;
  seed: ComposerSeed;
  onClose: () => void;
  onSent: () => void;
}) {
  const [to, setTo] = useState(seed.to ?? "");
  const [subject, setSubject] = useState(seed.subject ?? "");
  const [body, setBody] = useState(seed.body ?? "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    toRef.current?.focus();
  }, []);

  const send = async () => {
    const recipients = parseAddressList(to);
    if (recipients.length === 0) {
      setError("Add at least one recipient.");
      return;
    }
    // `from` is left out: the core fills it from the active account, and the
    // server picks the sending identity, files the draft and moves it to Sent.
    const draft: OutgoingEmailDraft = {
      to: recipients,
      cc: [],
      bcc: [],
      subject,
      body,
      inReplyTo: seed.inReplyTo ?? [],
      references: seed.references ?? [],
    };
    setSending(true);
    setError(null);
    try {
      await shell.command({ op: "send", draft });
      onSent();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSending(false);
    }
  };

  const fieldClass =
    "w-full border-0 border-b border-border/70 bg-transparent px-0 py-2 text-sm outline-none placeholder:text-muted-foreground/60 focus:border-ring";
  const title = seed.inReplyTo?.length ? "Reply" : "New message";
  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === "Escape") {
      onClose();
    } else if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void send();
    }
  };

  if (compact) {
    return (
      <div
        role="dialog"
        aria-label="Compose message"
        aria-modal="true"
        data-composer="sheet"
        onKeyDown={onKeyDown}
        className="fixed inset-0 z-50 flex flex-col bg-background text-foreground"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "env(safe-area-inset-left, 0px)",
          paddingRight: "env(safe-area-inset-right, 0px)",
          height: "100dvh",
        }}
      >
        <div className="sticky top-0 flex min-h-14 shrink-0 items-center gap-2 border-b border-border/70 bg-background px-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 rounded-full px-3 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            Cancel
          </button>
          <h2 className="min-w-0 flex-1 truncate text-center font-display text-base tracking-tight">
            {title}
          </h2>
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending}
            className="min-h-10 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col px-4 pt-1">
          <input
            ref={toRef}
            type="text"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            placeholder="To"
            aria-label="To"
            autoCapitalize="none"
            autoCorrect="off"
            inputMode="email"
            className={cn(fieldClass, "min-h-11 text-base")}
          />
          <input
            type="text"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder="Subject"
            aria-label="Subject"
            className={cn(fieldClass, "min-h-11 text-base")}
          />
          <textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="Write your message"
            aria-label="Message body"
            className="mt-2 min-h-0 flex-1 resize-none bg-transparent px-0 py-1 text-base leading-relaxed outline-none placeholder:text-muted-foreground/60"
          />
          {error ? (
            <p role="alert" className="py-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Compose message"
      data-composer="dialog"
      onKeyDown={onKeyDown}
      className="fixed bottom-5 right-5 z-50 flex w-[34rem] max-w-[calc(100vw-2.5rem)] flex-col rounded-xl border border-border bg-background shadow-2xl"
    >
      <div className="flex items-center justify-between rounded-t-xl bg-muted/60 px-4 py-2.5">
        <h2 className="font-display text-sm tracking-tight">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close composer"
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X aria-hidden className="h-4 w-4" />
        </button>
      </div>
      <div className="flex flex-col px-4 pb-4 pt-1">
        <input
          ref={toRef}
          type="text"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          placeholder="To"
          aria-label="To"
          className={fieldClass}
        />
        <input
          type="text"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          placeholder="Subject"
          aria-label="Subject"
          className={fieldClass}
        />
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          placeholder="Write your message"
          aria-label="Message body"
          rows={10}
          className="mt-2 resize-y bg-transparent px-0 py-1 text-sm leading-relaxed outline-none placeholder:text-muted-foreground/60"
        />
        {error ? (
          <p role="alert" className="mt-1 text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">⌘⏎ to send</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={() => void send()}
              disabled={sending}
              className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
